import React, { useEffect, useMemo, useRef, useState } from 'react';
import Head from 'next/head';
import {
  Sticker, ScanLine, BellRing, Lock, Zap, Smartphone, Camera, ShieldCheck, Rocket,
  Truck, FileText, Repeat, TriangleAlert, Car, ShieldAlert, Siren, KeyRound, Trash2,
  MapPinOff, UserX, Scale, ShieldHalf, Search, MessageCircle, Cookie, ExternalLink,
  CreditCard, Globe, ArrowRight, Menu, X, Check, Info, HelpCircle, Star, type LucideIcon,
} from 'lucide-react';
import HeroDemo from '../components/landing/HeroDemo';
import Stats from '../components/landing/Stats';
import HowItWorks from '../components/landing/HowItWorks';
import Features from '../components/landing/Features';
import AlertTypes from '../components/landing/AlertTypes';
import Security from '../components/landing/Security';
import Roadmap from '../components/landing/Roadmap';
import StoreTeaser from '../components/landing/StoreTeaser';
import Reviews from '../components/landing/Reviews';
import MembershipCta from '../components/landing/MembershipCta';
import Faq from '../components/landing/Faq';
import DemoSection from '../components/landing/DemoSection';
import Logo from '../components/Logo';
import LocaleToggle from '../components/LocaleToggle';
import { useLocale } from '../context/LocaleContext';
import { getLandingContent, buildJsonLd, type PkgItem } from '../i18n/landing';

const SITE_URL = process.env.NEXT_PUBLIC_SITE_URL || 'https://parkmesaj.com';


interface FeaturedFeedback {
  id: string;
  rating: number;
  comment: string;
  createdAt: string;
}

/**
 * Kullanıcının bölgesini cihaz saat dilimi + dil tercihine göre belirler.
 * Türkiye → PAYTR (TL), diğer → DODOPAYMENTS (USD). Belirsizse Türkiye varsayılır.
 * Son kullanıcıya ödeme sağlayıcısı seçtirilmez; otomatik yönlendirilir.
 */
function detectPaymentProvider(): 'PAYTR' | 'DODOPAYMENTS' {
  if (typeof window === 'undefined') return 'PAYTR';
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || '';
    const langs = [navigator.language, ...(navigator.languages || [])]
      .filter(Boolean)
      .map((l) => l.toLowerCase());
    const isTR = tz === 'Europe/Istanbul' || langs.some((l) => l === 'tr' || l.startsWith('tr-') || l.endsWith('-tr'));
    return isTR ? 'PAYTR' : 'DODOPAYMENTS';
  } catch {
    return 'PAYTR';
  }
}

export default function Home() {
  const { locale, brandName } = useLocale();
  const L = useMemo(() => getLandingContent(locale, brandName), [locale, brandName]);
  const jsonLd = useMemo(() => buildJsonLd(L, brandName, SITE_URL), [L, brandName]);


  const canvasRef = useRef<HTMLCanvasElement>(null);

  const [showDownloadModal, setShowDownloadModal] = useState<boolean>(false);
  /* Mağaza linkleri admin panelden (system_config). iOS linki yokken "App Store'da yakında"
   * gösterilir — sitede .ipa dağıtımı ve sideload kılavuzu YOK (App Store dışı dağıtım izlenimi
   * Apple incelemesinde risk; doküman 48 §11). */
  const [iosAppUrl, setIosAppUrl] = useState<string>('');
  const [androidAppUrl, setAndroidAppUrl] = useState<string>('');
  const [mobileMenuOpen, setMobileMenuOpen] = useState<boolean>(false);

  const handleIOSClick = (e: React.MouseEvent) => {
    e.preventDefault();
    setShowDownloadModal(true);
  };

  const API_BASE_URL = process.env.NEXT_PUBLIC_API_URL || 'https://api.parkmesaj.com';

  // Cookie Consent States
  const [cookieConsent, setCookieConsent] = useState<string | null>(null);
  
  // Script IDs
  const [gaId, setGaId] = useState<string>('');
  const [pixelId, setPixelId] = useState<string>('');
  const [demoToken, setDemoToken] = useState<string>('a7X92b');

  // Checkout States
  const [showCheckout, setShowCheckout] = useState<boolean>(false);
  const [checkoutPackage, setCheckoutPackage] = useState<'INDIVIDUAL' | 'FAMILY' | 'CORPORATE' | 'STANDART' | 'KURUMSAL'>('INDIVIDUAL');
  const [checkoutEmail, setCheckoutEmail] = useState<string>('');
  const [checkoutName, setCheckoutName] = useState<string>('');
  const [checkoutPhone, setCheckoutPhone] = useState<string>('');
  const [checkoutCompany, setCheckoutCompany] = useState<string>('');
  const [vehicleCount, setVehicleCount] = useState<number>(10);
  /* Kurumsal korumalar (EP-13). Minimum araç sayısı backend'den geliyor — buraya
   * KOPYALANMIYOR (kopya limit bu projede üç kez saptı, CLAUDE.md). */
  const [corporateMinVehicles, setCorporateMinVehicles] = useState<number>(5);
  const [corporateTaxId, setCorporateTaxId] = useState<string>('');
  const [corporateCommitment, setCorporateCommitment] = useState<boolean>(false);
  const [paymentProvider, setPaymentProvider] = useState<'PAYTR' | 'DODOPAYMENTS'>('PAYTR');
  const [checkoutUrl, setCheckoutUrl] = useState<string | null>(null);
  // Mobil uygulamadan gelen kimlik doğrulamalı yükseltme (1.9) — ?upgradeToken=... ile taşınır.
  const [upgradeToken, setUpgradeToken] = useState<string | null>(null);
  /* Backend'in hesapladığı kalem dökümü (EP-07c / §9.1). Yerel `pkgPrice` yerine bunu
   * göstermek şart: donanımı ödemiş kullanıcıdan yalnız yenileme bedeli alınıyor, yerel
   * hesap ise her zaman ilk yıl toplamını basıyordu (400 gösterip 100 tahsil etme hatası). */
  /* Checkout upsell ürünleri (EP-08 · dok.42 §4.4) — `checkoutUpsell` işaretli ve aktif
   * ürünler. Fiyat burada yalnız GÖSTERİM için; tahsilatta backend config'ten okuyor. */
  const [upsellProducts, setUpsellProducts] = useState<{ id: string; name: string; priceTL: number; campaignPrice?: number }[]>([]);
  const [selectedUpsells, setSelectedUpsells] = useState<string[]>([]);

  const [priceQuote, setPriceQuote] = useState<{
    hardwareTL: number; serviceTL: number; shippingTL: number;
    amount: number; matrah: number; kdvAmount: number; isRenewal: boolean;
  } | null>(null);
  const [checkoutLoading, setCheckoutLoading] = useState<boolean>(false);
  const [corporateSuccess, setCorporateSuccess] = useState<boolean>(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  // Coupon States
  const [couponInput, setCouponInput] = useState<string>('');
  const [appliedCoupon, setAppliedCoupon] = useState<any | null>(null);
  const [couponError, setCouponError] = useState<string | null>(null);
  const [couponSuccess, setCouponSuccess] = useState<string | null>(null);
  const [couponLoading, setCouponLoading] = useState<boolean>(false);

  const handleApplyCoupon = async () => {
    if (!couponInput.trim()) return;
    setCouponLoading(true);
    setCouponError(null);
    setCouponSuccess(null);
    try {
      const res = await fetch(`${API_BASE_URL}/api/payments/validate-coupon`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code: couponInput.trim() })
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setAppliedCoupon(data.data);
        setCouponSuccess(`Kupon uygulandı! ${data.data.discountType === 'PERCENTAGE' ? `%${data.data.discountAmount}` : `${data.data.discountAmount} TL`} indirim kazandınız.`);
      } else {
        setCouponError(data.error?.message || 'Geçersiz kupon kodu.');
        setAppliedCoupon(null);
      }
    } catch (err) {
      setCouponError('Kupon doğrulanamadı.');
      setAppliedCoupon(null);
    } finally {
      setCouponLoading(false);
    }
  };

  // Pricing packages (admin tarafından düzenlenebilir, public config'ten gelir)
  const [packages, setPackages] = useState<PkgItem[]>(L.defaultPackages);

  useEffect(() => {
    setPackages(L.defaultPackages);
  }, [L.defaultPackages]);

  // Mobil uygulamadan "?upgradeToken=..." ile gelindiyse yakala — checkout'ta backend'e iletilir,
  // ödeme misafir e-posta eşleştirmesi yerine doğrudan bu (gerçek) hesaba bağlanır.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const params = new URLSearchParams(window.location.search);
    const t = params.get('upgradeToken');
    if (t) setUpgradeToken(t);
  }, []);

  /* Tahsil edilecek tutarı backend'den al (EP-07c). Kimlik varsa (upgradeToken) donanım
   * bedeli düşülmüş fiyat döner. İstek başarısız olursa `priceQuote` null kalır ve ekran
   * eski yerel hesaba düşer — vitrin hiçbir durumda boş kalmaz. */
  useEffect(() => {
    if (checkoutPackage === 'CORPORATE' || checkoutPackage === 'KURUMSAL') {
      setPriceQuote(null);
      return;
    }
    let cancelled = false;
    const qs = new URLSearchParams({ packageType: checkoutPackage });
    if (upgradeToken) qs.set('upgradeToken', upgradeToken);
    fetch(`${API_BASE_URL}/api/payments/quote?${qs.toString()}`)
      .then((r) => r.json())
      .then((json) => {
        if (!cancelled && json?.success && json.data) setPriceQuote(json.data);
      })
      .catch(() => { if (!cancelled) setPriceQuote(null); });
    return () => { cancelled = true; };
  }, [checkoutPackage, upgradeToken]);

  // Kullanıcı değerlendirmeleri (sosyal kanıt)
  const [feedbackAverage, setFeedbackAverage] = useState<number>(0);
  const [feedbackTotal, setFeedbackTotal] = useState<number>(0);
  const [featuredFeedbacks, setFeaturedFeedbacks] = useState<FeaturedFeedback[]>([]);

  // Seçili paketin tutarını config fiyatından hesaplar
  /** Seçili upsell ürünlerinin toplamı (kampanya fiyatı varsa o geçerli). */
  const upsellTotal = (): number =>
    upsellProducts
      .filter((pr) => selectedUpsells.includes(pr.id))
      .reduce((t, pr) => t + ((pr.campaignPrice && pr.campaignPrice > 0) ? pr.campaignPrice : pr.priceTL), 0);

  const pkgPrice = (currency: 'TL' | 'USD'): number => {
    // Backend teklifi varsa o esastır — tahsil edilecek tutarla ekrandaki tutar ayrışmasın.
    if (currency === 'TL' && priceQuote) return priceQuote.amount + upsellTotal();
    const pk = packages.find((p) => p.id === checkoutPackage);
    if (pk) {
      const unit = currency === 'TL' ? pk.priceTL : pk.priceUSD;
      return pk.id === 'CORPORATE' || pk.id === 'KURUMSAL' ? vehicleCount * unit : unit;
    }
    // API'den paket gelmediğinde son çare — ilk yıl toplamları (donanım + abonelik, §9.1).
    // Gerçek fiyat her zaman backend'den gelir; buradaki değerler yalnız ağ hatası anında görünür.
    const unit = checkoutPackage === 'FAMILY'
      ? (currency === 'TL' ? 600 : 24)
      : checkoutPackage === 'INDIVIDUAL' || checkoutPackage === 'STANDART'
        ? (currency === 'TL' ? 400 : 12)
        : (currency === 'TL' ? 70 : 3);
    return checkoutPackage === 'KURUMSAL' || checkoutPackage === 'CORPORATE' ? vehicleCount * unit : unit;
  };
  const getDiscountedPrice = (currency: 'TL' | 'USD'): number => {
    const base = pkgPrice(currency);
    if (!appliedCoupon) return base;
    const discount = appliedCoupon.discountType === 'PERCENTAGE'
      ? base * (appliedCoupon.discountAmount / 100)
      : appliedCoupon.discountAmount;
    return Math.max(1, Number((base - discount).toFixed(2)));
  };


  // 1. Fetch config for analytics IDs & Load consent
  useEffect(() => {
    async function fetchConfig() {
      try {
        const res = await fetch(`${API_BASE_URL}/api/config/public`);
        const json = await res.json();
        if (json.success && json.data) {
          setGaId(json.data.googleAnalyticsId || '');
          setPixelId(json.data.facebookPixelId || '');
          if (Array.isArray(json.data.packages)) setPackages(json.data.packages);
          if (json.data.demo?.qrToken) setDemoToken(json.data.demo.qrToken);
          if (typeof json.data.corporateMinVehicles === 'number') setCorporateMinVehicles(json.data.corporateMinVehicles);
          if (Array.isArray(json.data.products)) {
            setUpsellProducts(json.data.products.filter((pr: any) => pr.checkoutUpsell && pr.active));
          }
          if (json.data.dodoPaymentsEnabled === false) setPaymentProvider('PAYTR');
          setIosAppUrl(typeof json.data.iosAppUrl === 'string' ? json.data.iosAppUrl : '');
          setAndroidAppUrl(typeof json.data.androidAppUrl === 'string' ? json.data.androidAppUrl : '');
        }
      } catch (err) {
        console.error('Failed to fetch system configurations:', err);
      }
    }
    fetchConfig();

    async function fetchFeedbacks() {
      try {
        const res = await fetch(`${API_BASE_URL}/api/feedbacks/featured`);
        const json = await res.json();
        if (json.success && json.data) {
          setFeedbackAverage(json.data.average || 0);
          setFeedbackTotal(json.data.total || 0);
          setFeaturedFeedbacks(Array.isArray(json.data.featured) ? json.data.featured : []);
        }
      } catch (err) {
        console.error('Failed to fetch feedbacks:', err);
      }
    }
    fetchFeedbacks();

    const consent = localStorage.getItem('pingparking_cookie_consent');
    if (consent) {
      setCookieConsent(consent);
    }

    // Konuma göre ödeme yöntemini otomatik belirle (kullanıcıya seçtirilmez).
    setPaymentProvider(detectPaymentProvider());
  }, []);

  // 2. Inject GA & Pixel if accepted
  useEffect(() => {
    if (cookieConsent !== 'accepted') return;

    if (gaId) {
      const gaScript1 = document.createElement('script');
      gaScript1.async = true;
      gaScript1.src = `https://www.googletagmanager.com/gtag/js?id=${gaId}`;
      document.head.appendChild(gaScript1);

      const gaScript2 = document.createElement('script');
      gaScript2.innerHTML = `
        window.dataLayer = window.dataLayer || [];
        function gtag(){dataLayer.push(arguments);}
        gtag('js', new Date());
        gtag('config', '${gaId}');
      `;
      document.head.appendChild(gaScript2);
    }

    if (pixelId) {
      const pixelScript = document.createElement('script');
      pixelScript.innerHTML = `
        !function(f,b,e,v,n,t,s)
        {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
        n.callMethod.apply(n,arguments):n.queue.push(arguments)};
        if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
        n.queue=[];t=b.createElement(e);t.async=!0;
        t.src=v;s=b.getElementsByTagName(e)[0];
        s.parentNode.insertBefore(t,s)}(window, document,'script',
        'https://connect.facebook.net/en_US/fbevents.js');
        fbq('init', '${pixelId}');
        fbq('track', 'PageView');
      `;
      document.head.appendChild(pixelScript);
    }
  }, [cookieConsent, gaId, pixelId]);

  const handleAcceptCookies = () => {
    localStorage.setItem('pingparking_cookie_consent', 'accepted');
    setCookieConsent('accepted');
  };

  const handleDeclineCookies = () => {
    localStorage.setItem('pingparking_cookie_consent', 'declined');
    setCookieConsent('declined');
  };

  const handleStartCheckout = async (e: React.FormEvent) => {
    e.preventDefault();
    setCheckoutLoading(true);
    setErrorMsg(null);
    setCheckoutUrl(null);

    const isCorporate = checkoutPackage === 'KURUMSAL' || checkoutPackage === 'CORPORATE';

    const body = isCorporate
      ? {
          name: checkoutName,
          email: checkoutEmail,
          phone: checkoutPhone,
          companyName: checkoutCompany,
          vehicleCount: vehicleCount,
          taxId: corporateTaxId,
          commitmentAccepted: corporateCommitment,
        }
      : {
          packageType: checkoutPackage,
          email: checkoutEmail,
          name: checkoutName,
          phone: checkoutPhone,
          couponCode: appliedCoupon ? appliedCoupon.code : undefined,
          upgradeToken: upgradeToken || undefined,
          // Yalnız id gönderiliyor; fiyatı backend config'ten okuyor (manipülasyon önlemi).
          upsellProductIds: selectedUpsells.length > 0 ? selectedUpsells : undefined
        };

    const endpoint = isCorporate
      ? `${API_BASE_URL}/api/payments/corporate/request`
      : (paymentProvider === 'PAYTR'
          ? `${API_BASE_URL}/api/payments/paytr/session`
          : `${API_BASE_URL}/api/payments/dodo/session`);

    try {
      const res = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });
      const data = await res.json();
      if (res.ok && data.success) {
        if (isCorporate) {
          setCorporateSuccess(true);
        } else {
          if (paymentProvider === 'PAYTR') {
            setCheckoutUrl(data.data.iframeUrl);
          } else {
            setCheckoutUrl(data.data.checkoutUrl);
          }
        }
      } else {
        setErrorMsg(data.error?.message || (isCorporate ? 'Teklif talebi gönderilemedi. Lütfen bilgilerinizi kontrol edin.' : 'Ödeme oturumu başlatılamadı. Lütfen bilgilerinizi kontrol edin.'));
      }
    } catch (err) {
      console.error(err);
      setErrorMsg('Bir bağlantı hatası oluştu. Lütfen tekrar deneyin.');
    } finally {
      setCheckoutLoading(false);
    }
  };

  /* ── 1. Particle Canvas ─────────────────────────────────── */
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    // Respect users who prefer reduced motion: skip the animated particle field.
    if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let animId = 0;
    const particles: { x: number; y: number; vx: number; vy: number; r: number; o: number }[] = [];

    const resize = () => {
      canvas.width  = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resize();
    window.addEventListener('resize', resize);

    for (let i = 0; i < 80; i++) {
      particles.push({
        x: Math.random() * window.innerWidth,
        y: Math.random() * window.innerHeight,
        vx: (Math.random() - 0.5) * 0.35,
        vy: (Math.random() - 0.5) * 0.35,
        r: Math.random() * 1.5 + 0.5,
        o: Math.random() * 0.4 + 0.1,
      });
    }

    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      particles.forEach((p, i) => {
        p.x += p.vx;
        p.y += p.vy;
        if (p.x < 0) p.x = canvas.width;
        if (p.x > canvas.width) p.x = 0;
        if (p.y < 0) p.y = canvas.height;
        if (p.y > canvas.height) p.y = 0;

        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(255, 176, 32, ${p.o})`;
        ctx.fill();

        // Connect close particles
        for (let j = i + 1; j < particles.length; j++) {
          const q = particles[j];
          const dx = p.x - q.x, dy = p.y - q.y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < 120) {
            ctx.beginPath();
            ctx.moveTo(p.x, p.y);
            ctx.lineTo(q.x, q.y);
            ctx.strokeStyle = `rgba(245, 147, 0, ${0.12 * (1 - dist / 120)})`;
            ctx.lineWidth = 0.6;
            ctx.stroke();
          }
        }
      });
      animId = requestAnimationFrame(draw);
    };
    draw();

    return () => {
      cancelAnimationFrame(animId);
      window.removeEventListener('resize', resize);
    };
  }, []);

  /* ── 2. Scroll Reveal ───────────────────────────────────── */
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) e.target.classList.add('revealed');
        });
      },
      { threshold: 0.12 }
    );
    document.querySelectorAll('.reveal, .reveal-left, .reveal-right, .reveal-scale').forEach((el) =>
      observer.observe(el)
    );
    return () => observer.disconnect();
  }, [featuredFeedbacks, feedbackTotal]);

  /* ── 3. 3D Tilt on feature cards ───────────────────────── */
  useEffect(() => {
    const cards = document.querySelectorAll<HTMLElement>('.tilt-card');
    const handlers: Array<{ el: HTMLElement; onMove: (e: MouseEvent) => void; onLeave: () => void }> = [];

    cards.forEach((card) => {
      const onMove = (e: MouseEvent) => {
        const rect = card.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;
        const cx = rect.width / 2, cy = rect.height / 2;
        const rx = ((y - cy) / cy) * 8;
        const ry = ((cx - x) / cx) * 8;
        card.style.transform = `perspective(800px) rotateX(${rx}deg) rotateY(${ry}deg) scale(1.02)`;
      };
      const onLeave = () => {
        card.style.transform = 'perspective(800px) rotateX(0) rotateY(0) scale(1)';
      };
      card.addEventListener('mousemove', onMove);
      card.addEventListener('mouseleave', onLeave);
      handlers.push({ el: card, onMove, onLeave });
    });

    return () => {
      handlers.forEach(({ el, onMove, onLeave }) => {
        el.removeEventListener('mousemove', onMove);
        el.removeEventListener('mouseleave', onLeave);
      });
    };
  }, []);

  return (
    <>
      <Head>
        <title>{L.meta.title}</title>
        <meta name="description" content={L.meta.description} />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <link rel="canonical" href={SITE_URL} />
        <meta name="robots" content="index, follow" />
        <meta property="og:type" content="website" />
        <meta property="og:locale" content={L.meta.ogLocale} />
        <meta property="og:url" content={SITE_URL} />
        <meta property="og:site_name" content={brandName} />
        <meta property="og:title" content={L.meta.ogTitle} />
        <meta property="og:description" content={L.meta.ogDescription} />
        <meta property="og:image" content={`${SITE_URL}/og-image.png`} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={L.meta.twitterTitle} />
        <meta name="twitter:description" content={L.meta.twitterDescription} />
        <meta name="twitter:image" content={`${SITE_URL}/og-image.png`} />
        <link rel="icon" href="/assets/park-mesaj-logo-nobg.png" type="image/png" />
        <link rel="apple-touch-icon" href="/assets/park-mesaj-logo-nobg.png" />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
        />
      </Head>

      {/* Particle Canvas kaldırıldı — "Sinyal" yönü: tam-sayfa yıldız tarlası yerine hero'da tek amber ışıma;
          negatif alan bilinçli. canvasRef guard'lı olduğu için effect no-op olur. */}

      {/* ── NAV ─────────────────────────────────────────────── */}
      <nav className="nav-blur fixed top-0 left-0 right-0 z-50">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <a href="#" className="group">
            <Logo size={44} className="group-hover:scale-105 transition-transform" />
          </a>

          <div className="hidden md:flex items-center gap-8">
            {L.nav.map((l) => (
              <a
                key={l.href}
                href={l.href}
                className="text-sm text-gray-400 hover:text-[#FFB020] transition-colors duration-200"
              >
                {l.label}
              </a>
            ))}
          </div>

          <div className="flex items-center gap-3">
            <LocaleToggle className="hidden sm:flex" />
            {/* Kurumsal panel girişi menüden çekildi (doküman 48 §11 K3): /corporate/login
                doğrudan URL'de yaşıyor, müşterilere link veriliyor. */}
            <a
              href="#demo"
              id="nav-cta-btn"
              className="hidden sm:inline-flex btn-shimmer text-white text-sm font-semibold px-5 py-2 rounded-full whitespace-nowrap flex-shrink-0"
            >
              {L.navUi.demo}
            </a>
            <button
              onClick={() => setMobileMenuOpen((o) => !o)}
              className="md:hidden text-gray-300 hover:text-white p-1.5"
              aria-label={L.navUi.menuAria}
              aria-expanded={mobileMenuOpen}
            >
              {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        {mobileMenuOpen && (
          <div className="md:hidden border-t border-white/5 bg-[rgba(13,17,23,0.97)] backdrop-blur-xl">
            <div className="px-6 py-4 flex flex-col gap-1">
              {L.nav.map((l) => (
                <a
                  key={l.href}
                  href={l.href}
                  onClick={() => setMobileMenuOpen(false)}
                  className="text-sm text-gray-300 hover:text-[#FFB020] py-2.5 border-b border-white/5 transition-colors"
                >
                  {l.label}
                </a>
              ))}
              <div className="flex gap-3 pt-4">
                <a
                  href="#demo"
                  onClick={() => setMobileMenuOpen(false)}
                  className="flex-1 text-center btn-shimmer text-white text-sm font-semibold px-4 py-2.5 rounded-xl"
                >
                  {L.navUi.demo}
                </a>
              </div>
            </div>
          </div>
        )}
      </nav>

      <main className="relative z-10">

        {/* ── HERO ──────────────────────────────────────────── */}
        <section
          id="hero"
          className="hero-signal-bg relative min-h-screen flex items-center overflow-hidden pt-16"
        >
          {/* Background blobs */}
          <div className="blob blob-1 w-96 h-96 top-20 left-10 opacity-60" />
          <div className="blob blob-2 w-80 h-80 top-1/2 right-20 opacity-50" />
          <div className="blob blob-3 w-64 h-64 bottom-20 left-1/3 opacity-40" />

          <div className="max-w-7xl mx-auto px-6 py-24 grid grid-cols-1 lg:grid-cols-2 gap-16 items-center w-full">
            {/* Left — Text */}
            <div>
              {/* Badge */}
              <div className="inline-flex items-center gap-2 glass-panel px-4 py-2 rounded-full mb-8 border border-[rgba(255,176,32,0.28)]">
                <span className="w-2 h-2 rounded-full bg-[#FFB020] animate-pulse" />
                <span className="text-xs text-gray-300 font-medium tracking-widest uppercase">
                  {L.hero.badge}
                </span>
              </div>

              <h1
                className="text-5xl md:text-6xl lg:text-7xl font-black leading-[1.08] tracking-tight mb-6"
                style={{ fontFamily: 'var(--font-heading)' }}
              >
                {L.hero.h1.map((line, i) => (
                  <span key={i} className={`block ${i === 1 ? 'gradient-amber' : 'text-white'}`}>{line}</span>
                ))}
              </h1>

              <p className="text-gray-400 text-lg leading-relaxed mb-10 max-w-lg">
                {L.hero.subtitle}
              </p>

              <div className="flex flex-wrap gap-4 items-center">
                <a
                  href="#demo"
                  id="hero-primary-btn"
                  className="btn-signal font-bold px-8 py-4 rounded-2xl text-base shadow-xl inline-flex items-center gap-2"
                >
                  <Rocket size={18} /> {L.hero.tryFree}
                </a>
                
                {/* Android & iOS side-by-side app download buttons */}
                <div className="flex flex-wrap gap-3">
                  <button
                    onClick={() => setShowDownloadModal(true)}
                    id="hero-android-btn"
                    className="text-white font-bold px-5 py-3 rounded-2xl text-sm shadow-xl flex items-center gap-2.5 hover:scale-105 transition-transform text-left"
                    style={{
                      background: 'linear-gradient(135deg, rgba(61,220,132,0.15) 0%, rgba(255, 176, 32,0.1) 100%)',
                      border: '1.5px solid rgba(61,220,132,0.4)',
                      boxShadow: '0 4px 20px rgba(61,220,132,0.15)'
                    }}
                  >
                    <svg className="w-5 h-5 fill-[#3DDC84]" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path d="M17.523 15.3l1.816 3.146a.9.9 0 0 1-.312 1.223.9.9 0 0 1-1.229-.31L15.93 16.12A11.75 11.75 0 0 1 12 16.8a11.75 11.75 0 0 1-3.93-.68L6.208 19.36a.9.9 0 0 1-1.229.31.9.9 0 0 1-.312-1.223L6.477 15.3C3.673 13.725 1.756 10.79 1.63 7.378h20.74c-.126 3.412-2.043 6.347-4.847 7.922zM7.07 10.24a.82.82 0 1 0 0-1.64.82.82 0 0 0 0 1.64zm9.86 0a.82.82 0 1 0 0-1.64.82.82 0 0 0 0 1.64z"/>
                    </svg>
                    <div>
                      <div className="text-[9px] text-gray-400 font-normal uppercase tracking-wider leading-none mb-0.5">{L.hero.androidFor}</div>
                      <div className="text-xs font-black leading-none">{L.hero.androidDownload}</div>
                    </div>
                  </button>

                  <button
                    onClick={handleIOSClick}
                    id="hero-ios-btn"
                    className="text-white font-bold px-5 py-3 rounded-2xl text-sm shadow-xl flex items-center gap-2.5 hover:scale-105 transition-transform text-left"
                    style={{
                      background: 'linear-gradient(135deg, rgba(255,255,255,0.05) 0%, rgba(255,255,255,0.02) 100%)',
                      border: '1.5px solid rgba(255,255,255,0.2)',
                      boxShadow: '0 4px 20px rgba(0,0,0,0.3)'
                    }}
                  >
                    <svg className="w-5 h-5 fill-white" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M15.97 4.17c.66-.81 1.11-1.93.99-3.06-1 .04-2.22.67-2.94 1.5-.63.73-1.18 1.87-1.03 2.97 1.12.09 2.27-.56 2.98-1.41z"/>
                    </svg>
                    <div>
                      <div className="text-[9px] text-gray-400 font-normal uppercase tracking-wider leading-none mb-0.5">{L.hero.iosFor}</div>
                      <div className="text-xs font-black leading-none">{L.hero.iosDownload}</div>
                    </div>
                  </button>
                </div>

                <a
                  href="#how-it-works"
                  id="hero-secondary-btn"
                  className="btn-ghost text-white font-semibold px-8 py-4 rounded-2xl text-base inline-flex items-center gap-2"
                >
                  {L.hero.howItWorks} <ArrowRight size={16} />
                </a>
              </div>

              <p className="mt-6 text-xs text-gray-500">
                {L.hero.appsSoon}
              </p>
            </div>

            {/* Right — İnteraktif ürün demosu (statik görsel yerine) */}
            <div className="flex items-center justify-center">
              <HeroDemo />
            </div>
          </div>

          {/* Scroll hint */}
          <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 animate-bounce">
            <span className="text-xs text-gray-500 tracking-widest uppercase">{L.hero.scroll}</span>
            <div className="w-0.5 h-8 bg-gradient-to-b from-[#FFB020] to-transparent rounded-full" />
          </div>
        </section>

        {/* ── STATS ─────────────────────────────────────────── */}
        <Stats L={L} />
        {/* ── HOW IT WORKS ──────────────────────────────────── */}
        <HowItWorks L={L} />
        {/* ── FEATURES ──────────────────────────────────────── */}
        <Features L={L} />
        {/* ── ALERT TYPES ───────────────────────────────────── */}
        <AlertTypes L={L} />
        {/* ── SECURITY ──────────────────────────────────────── */}
        <Security L={L} />
        {/* ── ROADMAP TEASER ────────────────────────────────── */}
        <Roadmap L={L} />
        {/* ── CAM ETİKETİ KOLEKSİYONU (PRD-53) ─────────────── */}
        <StoreTeaser L={L} />
        {/* ── REVIEWS ───────────────────────────────────────── */}
        <Reviews L={L} feedbackAverage={feedbackAverage} feedbackTotal={feedbackTotal} featuredFeedbacks={featuredFeedbacks} />
        {/* ── ÜYELİK / KURUMSAL TEKLİF (eski Pricing; doküman 48 §11 K4) ──────── */}
        <MembershipCta L={L} setCheckoutPackage={setCheckoutPackage} setShowCheckout={setShowCheckout} setCheckoutUrl={setCheckoutUrl} setErrorMsg={setErrorMsg} setCorporateSuccess={setCorporateSuccess} setShowDownloadModal={setShowDownloadModal} />
        {/* ── FAQ (SEO + GEO) ─────────────────────────────────── */}
        <Faq L={L} />
        {/* ── LIVE DEMO / CTA ───────────────────────────────── */}
        <DemoSection L={L} demoToken={demoToken} />
      </main>

      {/* ── FOOTER ────────────────────────────────────────── */}
      <footer className="relative z-10 border-t border-white/5 py-12">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-12 mb-12">
            {/* Brand */}
            <div className="col-span-1 md:col-span-2">
              <div className="mb-4">
                <Logo size={40} />
              </div>
              <p className="text-gray-500 text-sm leading-relaxed max-w-xs">
                {L.footer.tagline}
              </p>
              <p className="mt-4 text-xs text-gray-600 font-semibold tracking-widest uppercase">
                {L.footer.subtitle}
              </p>
              <address className="mt-6 not-italic text-xs text-gray-500 leading-relaxed max-w-md">
                <span className="text-gray-400 font-semibold">{L.footer.seller.title}: </span>
                {L.footer.seller.name}
                <br />
                {L.footer.seller.address}
                <br />
                {L.footer.seller.contact}
              </address>
            </div>

            {/* Links */}
            <div>
              <h4 className="text-white font-semibold mb-4 text-sm" style={{ fontFamily: 'var(--font-heading)' }}>{L.footer.product}</h4>
              <ul className="space-y-2">
                {L.footer.productLinks.map((l) => (
                  <li key={l.href}>
                    <a href={l.href} className="text-gray-500 text-sm hover:text-[#FFB020] transition-colors">
                      {l.label}
                    </a>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <h4 className="text-white font-semibold mb-4 text-sm" style={{ fontFamily: 'var(--font-heading)' }}>{L.footer.legal}</h4>
              <ul className="space-y-2">
                {L.footer.legalLinks.map((l) => (
                  <li key={l.href}>
                    <a href={l.href} className="text-gray-500 text-sm hover:text-[#FFB020] transition-colors">
                      {l.label}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className="divider-glow" />

          <div className="pt-8 flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-gray-600 text-xs">
              © {new Date().getFullYear()} {brandName}. {L.footer.copyright}
            </p>
            <div className="flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-[#34C3A0] animate-pulse" />
              <span className="text-xs text-gray-500">{L.footer.serviceActive}</span>
            </div>
          </div>
        </div>
      </footer>

      {/* ── CHECKOUT MODAL ────────────────────────────────── */}
      {showCheckout && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 backdrop-blur-md p-4 overflow-y-auto">
          <div className="relative glass-panel rounded-3xl w-full max-w-lg p-6 md:p-8 gradient-border my-8">
            {/* Close Button */}
            <button
              onClick={() => {
                setShowCheckout(false);
                setCheckoutUrl(null);
                setCouponInput('');
                setAppliedCoupon(null);
                setCouponError(null);
                setCouponSuccess(null);
              }}
              className="absolute top-4 right-4 text-gray-400 hover:text-white text-xl font-bold bg-white/5 hover:bg-white/10 w-8 h-8 rounded-full flex items-center justify-center transition-colors"
            >
              ✕
            </button>

            {corporateSuccess ? (
              <div className="text-center py-10 space-y-6">
                <div className="w-16 h-16 bg-[#FFB020]/10 border border-[#FFB020]/30 rounded-full flex items-center justify-center mx-auto text-[#FFB020] animate-bounce">
                  <Check size={32} />
                </div>
                <div className="space-y-2">
                  <h3 className="text-2xl font-black text-white" style={{ fontFamily: 'var(--font-heading)' }}>
                    Talebiniz Alındı!
                  </h3>
                  <p className="text-gray-400 text-sm max-w-sm mx-auto leading-relaxed">
                    {L.checkout.corporateSuccess}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setShowCheckout(false);
                    setCorporateSuccess(false);
                  }}
                  className="bg-[#FFB020] hover:bg-[#D98A00] text-black font-bold px-8 py-3 rounded-xl transition-all shadow-lg shadow-[#FFB020]/20"
                >
                  Kapat
                </button>
              </div>
            ) : checkoutUrl ? (
              <div className="space-y-4 pt-4">
                <h3 className="text-xl font-bold text-white text-center" style={{ fontFamily: 'var(--font-heading)' }}>
                  Güvenli Ödeme Ekranı
                </h3>
                
                {paymentProvider === 'PAYTR' ? (
                  <div className="rounded-2xl overflow-hidden bg-white/5 p-1 border border-white/10">
                    <iframe
                      src={checkoutUrl}
                      style={{ width: '100%', height: '520px', border: 'none' }}
                      title="Güvenli Ödeme"
                    />
                  </div>
                ) : (
                  <div className="text-center py-10 space-y-6">
                    <p className="text-gray-400">Uluslararası güvenli ödeme sayfasına yönlendirileceksiniz. Ödemenizi tamamlamak için aşağıdaki butona tıklayın.</p>
                    <a
                      href={checkoutUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 bg-[#FFB020] hover:bg-[#D98A00] text-black font-bold px-8 py-3 rounded-xl transition-all shadow-lg shadow-[#FFB020]/20"
                    >
                      Ödemeye Devam Et <ArrowRight size={16} />
                    </a>
                  </div>
                )}
                
                <p className="text-xs text-gray-500 text-center">
                  Ödeme tamamlandıktan sonra sisteme kayıt e-posta adresiniz ile giriş yapabilirsiniz.
                </p>
              </div>
            ) : (
              <form onSubmit={handleStartCheckout} className="space-y-4 pt-4">
                <div className="text-center">
                  <h3 className="text-2xl font-black text-white" style={{ fontFamily: 'var(--font-heading)' }}>
                    {(checkoutPackage === 'KURUMSAL' || checkoutPackage === 'CORPORATE') ? 'Kurumsal Teklif Talebi' : 'Ödeme Bilgileri'}
                  </h3>
                  <p className="text-xs text-gray-400 mt-1">
                    {checkoutPackage === 'FAMILY'
                      ? 'Aile Paketi Satın Alımı'
                      : checkoutPackage === 'INDIVIDUAL' || checkoutPackage === 'STANDART'
                      ? 'Bireysel Paket Satın Alımı'
                      : 'Kurumsal Filo Paketi Teklif Talebi'}
                  </p>
                  {upgradeToken && (
                    <p className="text-[11px] text-emerald-400 mt-2 bg-emerald-500/10 border border-emerald-500/20 rounded-lg px-3 py-1.5 inline-block">
                      ✓ Ödeme mevcut uygulama hesabınıza bağlanacak
                    </p>
                  )}
                </div>

                {errorMsg && (
                  <div className="bg-red-500/10 border border-red-500/30 text-red-400 rounded-xl p-3 text-xs text-center font-medium">
                    {errorMsg}
                  </div>
                )}

                <div className="space-y-3">
                  <div>
                    <label className="block text-xs font-semibold text-gray-400 mb-1">Ad Soyad</label>
                    <input
                      type="text"
                      required
                      value={checkoutName}
                      onChange={(e) => setCheckoutName(e.target.value)}
                      placeholder="Ahmet Yılmaz"
                      className="w-full bg-[#161B27] border border-white/10 rounded-xl px-4 py-2.5 text-sm text-white focus:outline-none focus:border-[#FFB020] transition-colors"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-gray-400 mb-1">E-posta Adresi</label>
                    <input
                      type="email"
                      required
                      value={checkoutEmail}
                      onChange={(e) => setCheckoutEmail(e.target.value)}
                      placeholder="ahmet@mail.com"
                      className="w-full bg-[#161B27] border border-white/10 rounded-xl px-4 py-2.5 text-sm text-white focus:outline-none focus:border-[#FFB020] transition-colors"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-gray-400 mb-1">Telefon Numarası</label>
                    <input
                      type="tel"
                      required
                      value={checkoutPhone}
                      onChange={(e) => setCheckoutPhone(e.target.value)}
                      placeholder="+905554443322"
                      className="w-full bg-[#161B27] border border-white/10 rounded-xl px-4 py-2.5 text-sm text-white focus:outline-none focus:border-[#FFB020] transition-colors"
                    />
                  </div>

                  {(checkoutPackage === 'KURUMSAL' || checkoutPackage === 'CORPORATE') && (
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-semibold text-gray-400 mb-1">Firma Adı</label>
                        <input
                          type="text"
                          required
                          value={checkoutCompany}
                          onChange={(e) => setCheckoutCompany(e.target.value)}
                          placeholder="Lojistik A.Ş."
                          className="w-full bg-[#161B27] border border-white/10 rounded-xl px-4 py-2.5 text-sm text-white focus:outline-none focus:border-[#FFB020] transition-colors"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-gray-400 mb-1">Araç Adedi</label>
                        <input
                          type="number"
                          min={corporateMinVehicles}
                          required
                          value={vehicleCount}
                          onChange={(e) => setVehicleCount(Math.max(1, parseInt(e.target.value, 10) || 1))}
                          className="w-full bg-[#161B27] border border-white/10 rounded-xl px-4 py-2.5 text-sm text-white focus:outline-none focus:border-[#FFB020] transition-colors"
                        />
                        {vehicleCount < corporateMinVehicles && (
                          <p className="text-[10px] text-amber-400 mt-1">
                            Kurumsal paket en az {corporateMinVehicles} araç içindir. Daha az aracınız varsa Bireysel veya Aile paketine bakabilirsiniz.
                          </p>
                        )}
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-gray-400 mb-1">Vergi Kimlik No</label>
                        <input
                          type="text"
                          inputMode="numeric"
                          required
                          value={corporateTaxId}
                          onChange={(e) => setCorporateTaxId(e.target.value.replace(/\D/g, '').slice(0, 11))}
                          placeholder="10 veya 11 hane"
                          className="w-full bg-[#161B27] border border-white/10 rounded-xl px-4 py-2.5 text-sm text-white focus:outline-none focus:border-[#FFB020] transition-colors"
                        />
                      </div>
                    </div>
                  )}

                  {/* 1 yıl taahhüt onayı (EP-13 · dok.42 §3.1) — onay kaydı olmadan
                      admin panelinden aktivasyon da yapılamıyor. */}
                  {(checkoutPackage === 'KURUMSAL' || checkoutPackage === 'CORPORATE') && (
                    <label className="flex items-start gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        required
                        checked={corporateCommitment}
                        onChange={(e) => setCorporateCommitment(e.target.checked)}
                        className="mt-0.5"
                      />
                      <span className="text-[10px] text-gray-500 leading-snug">
                        Kurumsal aboneliğin <strong className="text-gray-400">1 yıllık taahhüt</strong> ile başladığını ve dönem içinde iptal hâlinde iade yapılmadığını kabul ediyorum. Teklif onayı sonrası vergi levhası doğrulaması istenebilir.
                      </span>
                    </label>
                  )}

                  {(checkoutPackage === 'KURUMSAL' || checkoutPackage === 'CORPORATE') ? (
                    <div className="flex items-center gap-3 rounded-xl border border-white/10 bg-white/[0.02] px-4 py-3">
                      <span className="w-8 h-8 rounded-lg bg-[#FFB020]/10 border border-[#FFB020]/20 flex items-center justify-center shrink-0">
                        <Info size={15} className="text-[#FFB020]" />
                      </span>
                      <div className="min-w-0">
                        <div className="text-xs font-semibold text-white">
                          Kurumsal Teklif Süreci
                        </div>
                        <div className="text-[10px] text-gray-500 leading-snug">
                          Talebinizi aldıktan sonra ekibimiz 24 saat içinde özel fiyat teklifi hazırlayacaktır.
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center gap-3 rounded-xl border border-white/10 bg-white/[0.02] px-4 py-3">
                      <span className="w-8 h-8 rounded-lg bg-[#FFB020]/10 border border-[#FFB020]/20 flex items-center justify-center shrink-0">
                        {paymentProvider === 'PAYTR'
                          ? <CreditCard size={15} className="text-[#FFB020]" />
                          : <Globe size={15} className="text-[#FFB020]" />}
                      </span>
                      <div className="min-w-0">
                        <div className="text-xs font-semibold text-white">
                          {paymentProvider === 'PAYTR' ? 'Türkiye · Güvenli TL Ödemesi' : 'Uluslararası · Güvenli USD Ödemesi'}
                        </div>
                        <div className="text-[10px] text-gray-500 leading-snug">
                          Konumunuza göre en uygun güvenli ödeme yöntemi otomatik seçildi.
                        </div>
                      </div>
                    </div>
                  )}

                  {!(checkoutPackage === 'KURUMSAL' || checkoutPackage === 'CORPORATE') && (
                    <div className="space-y-1.5 pt-1">
                      <label className="block text-xs font-semibold text-gray-400">İndirim Kodu / Kupon</label>
                      <div className="flex gap-2">
                        <input
                          type="text"
                          value={couponInput}
                          onChange={(e) => setCouponInput(e.target.value)}
                          placeholder="Örn: CEM20"
                          className="flex-1 bg-[#161B27] border border-white/10 rounded-xl px-4 py-2 text-sm text-white focus:outline-none focus:border-[#FFB020] uppercase transition-colors"
                        />
                        <button
                          type="button"
                          onClick={handleApplyCoupon}
                          disabled={couponLoading}
                          className="bg-[#FFB020]/10 hover:bg-[#FFB020]/20 border border-[#FFB020]/30 hover:border-[#FFB020]/50 text-[#FFB020] px-4 py-2 rounded-xl text-xs font-bold transition-all disabled:opacity-50"
                        >
                          {couponLoading ? '...' : 'Uygula'}
                        </button>
                      </div>
                      {couponError && <p className="text-red-400 text-[10px] font-medium pl-1">{couponError}</p>}
                      {couponSuccess && <p className="text-emerald-400 text-[10px] font-medium pl-1">{couponSuccess}</p>}
                    </div>
                  )}
                </div>

                <div className="pt-3 border-t border-white/5 space-y-2">
                  {/* Kalem dökümü (§9.1): başlıkta tek rakam, altında donanım/üyelik ayrımı.
                      Tutarlar backend'den geliyor — yenileyen kullanıcıda donanım satırı hiç
                      çıkmaz, çünkü ondan yalnız üyelik bedeli tahsil edilir. */}
                  {priceQuote && !appliedCoupon && paymentProvider === 'PAYTR' && (
                    <div className="space-y-1 mb-2 text-[11px]">
                      {priceQuote.hardwareTL > 0 && (
                        <div className="flex justify-between text-gray-500">
                          <span>QR etiket (tek seferlik)</span>
                          <span>{priceQuote.hardwareTL.toLocaleString('tr-TR')} TL</span>
                        </div>
                      )}
                      <div className="flex justify-between text-gray-500">
                        <span>Premium üyelik (1 yıl)</span>
                        <span>{priceQuote.serviceTL.toLocaleString('tr-TR')} TL</span>
                      </div>
                      <div className="flex justify-between text-gray-500">
                        <span>Kargo</span>
                        <span>{priceQuote.shippingTL > 0 ? `${priceQuote.shippingTL.toLocaleString('tr-TR')} TL` : 'Ücretsiz'}</span>
                      </div>
                      {priceQuote.isRenewal && (
                        <p className="text-emerald-400/80 pt-1">Etiket bedeli daha önce ödendiği için tekrar alınmıyor.</p>
                      )}
                      {selectedUpsells.length > 0 && upsellProducts
                        .filter((pr) => selectedUpsells.includes(pr.id))
                        .map((pr) => {
                          const price = (pr.campaignPrice && pr.campaignPrice > 0) ? pr.campaignPrice : pr.priceTL;
                          return (
                            <div key={pr.id} className="flex justify-between text-gray-500">
                              <span>{pr.name}</span>
                              <span>{price.toLocaleString('tr-TR')} TL</span>
                            </div>
                          );
                        })}
                    </div>
                  )}

                  {/* Checkout upsell (EP-08 · dok.42 §4.4) — aynı kargoya girdiği için ek
                      maliyet yok; sonradan gönderilen kampanyadan kat kat yüksek dönüşüyor.
                      Yalnız TL akışında: ürün gönderimi Türkiye içi. */}
                  {upsellProducts.length > 0 && paymentProvider === 'PAYTR'
                    && !(checkoutPackage === 'KURUMSAL' || checkoutPackage === 'CORPORATE') && (
                    <div className="space-y-2 mb-2">
                      {upsellProducts.map((pr) => {
                        const hasCampaign = !!(pr.campaignPrice && pr.campaignPrice > 0 && pr.campaignPrice < pr.priceTL);
                        const price = hasCampaign ? pr.campaignPrice! : pr.priceTL;
                        const checked = selectedUpsells.includes(pr.id);
                        return (
                          <label
                            key={pr.id}
                            className={`flex items-start gap-2 p-2.5 rounded-xl border cursor-pointer transition-colors ${
                              checked ? 'border-[#FFB020]/40 bg-[#FFB020]/[0.06]' : 'border-white/5 bg-white/[0.02] hover:border-white/10'
                            }`}
                          >
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={() => setSelectedUpsells((prev) =>
                                prev.includes(pr.id) ? prev.filter((x) => x !== pr.id) : [...prev, pr.id]
                              )}
                              className="mt-0.5"
                            />
                            <span className="text-[11px] leading-snug flex-1">
                              <span className="text-gray-300">{pr.name} ekleyin</span>
                              <span className="ml-1 text-[#FFB020] font-bold">{price.toLocaleString('tr-TR')} TL</span>
                              {hasCampaign && (
                                <span className="ml-1 text-gray-600 line-through">{pr.priceTL.toLocaleString('tr-TR')} TL</span>
                              )}
                              <span className="block text-gray-600">Aynı kargoya eklenir, ek kargo ücreti yok.</span>
                            </span>
                          </label>
                        );
                      })}
                    </div>
                  )}

                  {!(checkoutPackage === 'KURUMSAL' || checkoutPackage === 'CORPORATE') && (
                    <div className="flex justify-between items-center text-sm font-semibold mb-2">
                      <span className="text-gray-400">Toplam Tutar:</span>
                      {appliedCoupon ? (
                        <div className="flex flex-col items-end">
                          <span className="text-gray-500 line-through text-xs">
                            {paymentProvider === 'PAYTR' ? `${pkgPrice('TL')} TL` : `$${pkgPrice('USD')}`}
                          </span>
                          <span className="text-[#FFB020] text-lg font-black">
                            {paymentProvider === 'PAYTR' ? `${getDiscountedPrice('TL')} TL` : `$${getDiscountedPrice('USD')}`}
                          </span>
                        </div>
                      ) : (
                        <span className="text-white text-lg font-bold">
                          {paymentProvider === 'PAYTR' ? `${pkgPrice('TL')} TL` : `$${pkgPrice('USD')}`}
                        </span>
                      )}
                    </div>
                  )}
                  
                  <div className="flex items-start gap-2">
                    <input type="checkbox" required id="agree-checkbox" className="mt-1" />
                    {/* Cayma hakkı beyanı donanım ve hizmet için AYRI (PRD-05 / P20, doküman 43 §7):
                        eski metin paketin tamamını "dijital hizmet" sayıp cayma hakkı yok diyordu —
                        oysa kargoyla fiziksel etiket gönderiliyor ve onun 14 günlük cayma hakkı var. */}
                    <label htmlFor="agree-checkbox" className="text-[10px] text-gray-500 leading-snug">
                      <a href="/yasal/sozlesme" target="_blank" className="text-[#FFB020] hover:underline">Mesafeli Satış Sözleşmesi</a> ve{' '}
                      <a href="/yasal/kvkk" target="_blank" className="text-[#FFB020] hover:underline">KVKK Aydınlatma Metnini</a> okudum, onaylıyorum. Aboneliğin ödeme onayının ardından <strong className="text-gray-400">hemen başlatılmasını talep ediyorum</strong>; bu nedenle abonelik bedeli için cayma hakkımın sona erdiğini biliyorum. Kargoyla gönderilen fiziksel etiket için{' '}
                      <a href="/yasal/iade" target="_blank" className="text-[#FFB020] hover:underline">14 günlük cayma hakkım saklıdır</a>.
                    </label>
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={checkoutLoading}
                  className="w-full btn-shimmer py-3.5 rounded-xl font-bold text-white text-center shadow-lg transition-opacity flex items-center justify-center gap-2"
                >
                  {checkoutLoading ? (
                    <>
                      <span className="w-4 h-4 rounded-full border-2 border-white/20 border-t-white animate-spin" />
                      {(checkoutPackage === 'KURUMSAL' || checkoutPackage === 'CORPORATE') ? 'Talebiniz İletiliyor...' : 'Oturum Başlatılıyor...'}
                    </>
                  ) : (
                    (checkoutPackage === 'KURUMSAL' || checkoutPackage === 'CORPORATE') ? 'Teklif Talebini Gönder' : 'Güvenli Ödemeyi Başlat'
                  )}
                </button>

              </form>
            )}
          </div>
        </div>
      )}

      {/* ── DOWNLOAD MODAL ────────────────────────────────── */}
      {showDownloadModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 backdrop-blur-md p-4 overflow-y-auto">
          <div className="relative glass-panel rounded-3xl w-full max-w-lg p-6 md:p-8 gradient-border my-8">
            {/* Close Button */}
            <button
              onClick={() => setShowDownloadModal(false)}
              className="absolute top-4 right-4 text-gray-400 hover:text-white text-xl font-bold bg-white/5 hover:bg-white/10 w-8 h-8 rounded-full flex items-center justify-center transition-colors"
            >
              ✕
            </button>

            <div className="space-y-6 pt-4 text-center">
              <h3 className="text-2xl font-black text-white" style={{ fontFamily: 'var(--font-heading)' }}>
                Mobil Uygulamayı İndir
              </h3>
              <p className="text-gray-400 text-sm">
                ParkMesaj uygulamasını telefonunuza kurarak bildirimlerinizi anında anlık bildirim (Push Notification) olarak alabilirsiniz.
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {/* Android: Play linki girilmişse mağaza, yoksa geçici APK */}
                <a
                  href={androidAppUrl || '/downloads/pingparking.apk'}
                  target={androidAppUrl ? '_blank' : undefined}
                  rel={androidAppUrl ? 'noopener noreferrer' : undefined}
                  download={androidAppUrl ? undefined : true}
                  className="flex flex-col items-center justify-center gap-3 p-5 rounded-2xl bg-white/5 border border-[rgba(61,220,132,0.3)] hover:border-[#3DDC84]/60 transition-all hover:bg-white/10 group cursor-pointer"
                >
                  <svg className="w-10 h-10 fill-[#3DDC84] group-hover:scale-110 transition-transform" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path d="M17.523 15.3l1.816 3.146a.9.9 0 0 1-.312 1.223.9.9 0 0 1-1.229-.31L15.93 16.12A11.75 11.75 0 0 1 12 16.8a11.75 11.75 0 0 1-3.93-.68L6.208 19.36a.9.9 0 0 1-1.229.31.9.9 0 0 1-.312-1.223L6.477 15.3C3.673 13.725 1.756 10.79 1.63 7.378h20.74c-.126 3.412-2.043 6.347-4.847 7.922zM7.07 10.24a.82.82 0 1 0 0-1.64.82.82 0 0 0 0 1.64zm9.86 0a.82.82 0 1 0 0-1.64.82.82 0 0 0 0 1.64z"/>
                  </svg>
                  <div>
                    <div className="text-sm font-bold text-white">{androidAppUrl ? 'Google Play' : 'Android (APK)'}</div>
                    <div className="text-[10px] text-gray-500 mt-1">{androidAppUrl ? "Google Play'den indir" : 'Doğrudan APK İndir'}</div>
                  </div>
                </a>

                {/* iOS: yalnız App Store. Link yoksa pasif "yakında" kartı — .ipa dağıtımı yok. */}
                {iosAppUrl ? (
                  <a
                    href={iosAppUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex flex-col items-center justify-center gap-3 p-5 rounded-2xl bg-white/5 border border-white/10 hover:border-white/30 transition-all hover:bg-white/10 group cursor-pointer"
                  >
                    <svg className="w-10 h-10 fill-white group-hover:scale-110 transition-transform" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M15.97 4.17c.66-.81 1.11-1.93.99-3.06-1 .04-2.22.67-2.94 1.5-.63.73-1.18 1.87-1.03 2.97 1.12.09 2.27-.56 2.98-1.41z"/>
                    </svg>
                    <div>
                      <div className="text-sm font-bold text-white">App Store</div>
                      <div className="text-[10px] text-gray-500 mt-1">App Store&apos;dan indir</div>
                    </div>
                  </a>
                ) : (
                  <div
                    aria-disabled="true"
                    className="flex flex-col items-center justify-center gap-3 p-5 rounded-2xl bg-white/[0.03] border border-white/10 opacity-70 cursor-default"
                  >
                    <svg className="w-10 h-10 fill-white/70" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M15.97 4.17c.66-.81 1.11-1.93.99-3.06-1 .04-2.22.67-2.94 1.5-.63.73-1.18 1.87-1.03 2.97 1.12.09 2.27-.56 2.98-1.41z"/>
                    </svg>
                    <div>
                      <div className="text-sm font-bold text-white">App Store</div>
                      <div className="text-[10px] text-gray-500 mt-1">Çok yakında</div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── COOKIE CONSENT BANNER ──────────────────────────── */}
      {cookieConsent === null && (
        <div className="fixed bottom-6 right-6 z-[99] max-w-sm w-full p-1">
          <div className="glass-panel gradient-border rounded-2xl p-5 shadow-2xl relative overflow-hidden flex flex-col gap-4">
            <div>
              <h4 className="text-white text-sm font-bold mb-1 flex items-center gap-1.5" style={{ fontFamily: 'var(--font-heading)' }}>
                <Cookie size={16} className="text-[#FFB020]" /> Çerez Tercihleriniz
              </h4>
              <p className="text-xs text-gray-400 leading-relaxed">
                Deneyiminizi optimize etmek, Google Analytics ve Facebook Pixel analitiklerini entegre etmek için çerezler kullanmaktayız. Kabul ederek onay verebilirsiniz.
              </p>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={handleAcceptCookies}
                className="flex-1 bg-[#FFB020] hover:bg-[#D98A00] text-black font-bold py-2 rounded-lg text-xs transition-colors"
              >
                Kabul Et
              </button>
              <button
                onClick={handleDeclineCookies}
                className="flex-1 bg-white/5 hover:bg-white/10 text-gray-300 font-semibold py-2 rounded-lg text-xs transition-colors"
              >
                Reddet
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
